import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import kebabCase from 'lodash/kebabCase';

import { EVENT_ACTION_SWITCH_TAB } from 'approot/shared/data-tracker/data-events.constants';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import {
  Props,
  ARROW_DOWN,
  ARROW_UP,
  ARROW_LEFT,
  ARROW_RIGHT,
  HOME,
  END,
  ENTER,
  SPACEBAR,
  TabOption,
} from './tabs.constants';
import {
  TabsStyled,
  TabsTabStyled,
  TabsTabSkeletonStyled,
  TabsSkeletonStyled,
} from './tabs.style';

const Tabs = ({
  activeTab,
  options,
  onSelect,
  contentPanelId,
  ariaDescription,
  dataEvent,
  isLoading,
  fillWidth = true,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const getTabs = useCallback(
    () => (ref?.current ? [].slice.call(ref.current.childNodes) : []),
    []
  );

  const focusFirst = useCallback(() => setActiveIndex(0), [setActiveIndex]);

  const focusLast = useCallback(() => setActiveIndex(options.length - 1), [
    setActiveIndex,
    options.length,
  ]);

  const focusNext = useCallback(
    (currentIndex: number) => {
      if (currentIndex === options.length - 1) {
        focusFirst();
      } else {
        setActiveIndex(currentIndex + 1);
      }
    },
    [focusFirst, options.length]
  );

  const focusPrevious = useCallback(
    (currentIndex: number) => {
      if (currentIndex === 0) {
        focusLast();
      } else {
        setActiveIndex(currentIndex - 1);
      }
    },
    [focusLast, setActiveIndex]
  );

  const {
    category,
    target: { target_type, target_identifier },
  } = dataEvent || { target: {} };

  const handleOnSelect = useCallback(
    (option: TabOption) => {
      if (category && target_type && target_identifier) {
        pushDataTrackerEvent(category, EVENT_ACTION_SWITCH_TAB, {
          target_type,
          target_identifier,
          tab: kebabCase(option.label),
        });
      }
      onSelect(option);
    },
    [category, target_type, target_identifier, onSelect]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;

      switch (key) {
        case END: {
          event.preventDefault();
          event.stopPropagation();
          focusLast();
          break;
        }
        case HOME: {
          event.preventDefault();
          event.stopPropagation();
          focusFirst();
          break;
        }
        case ARROW_DOWN:
        case ARROW_RIGHT: {
          event.preventDefault();
          event.stopPropagation();
          focusNext(activeIndex);
          break;
        }
        case ARROW_UP:
        case ARROW_LEFT: {
          event.preventDefault();
          event.stopPropagation();
          focusPrevious(activeIndex);
          break;
        }
        case ENTER:
        case SPACEBAR: {
          event.preventDefault();
          event.stopPropagation();
          if (!options[activeIndex].isDisabled) {
            handleOnSelect(options[activeIndex]);
            const el = document.getElementById(contentPanelId);
            el?.focus({ preventScroll: true });
            setActiveIndex(-1);
          }
          break;
        }
        default:
          break;
      }
    },
    [
      activeIndex,
      contentPanelId,
      setActiveIndex,
      handleOnSelect,
      options,
      focusNext,
      focusPrevious,
      focusFirst,
      focusLast,
    ]
  );

  const handleOnClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      if (target.hasAttribute('disabled')) {
        return false;
      }

      const tabs = getTabs() as HTMLElement[];
      const index = tabs.findIndex(t => t === target);
      if (index !== -1) {
        setActiveIndex(-1);
        handleOnSelect(options[index]);
      }
    },
    [getTabs, handleOnSelect, options, setActiveIndex]
  );

  const handleOnFocus = useCallback(() => {
    const index = options.findIndex(option => option.value === activeTab);
    setActiveIndex(index);
  }, [options, activeTab, setActiveIndex]);

  if (isLoading) {
    return (
      <TabsSkeletonStyled>
        <TabsTabSkeletonStyled />
        <TabsTabSkeletonStyled />
        <TabsTabSkeletonStyled />
        <TabsTabSkeletonStyled />
      </TabsSkeletonStyled>
    );
  }

  return (
    <TabsStyled
      ref={ref}
      role="tablist"
      aria-label={ariaDescription}
      aria-activedescendant={
        activeIndex !== -1 ? `tab-${activeIndex}` : undefined
      }
      tabIndex={0}
      $fillWidth={fillWidth}
      onClick={handleOnClick}
      onFocus={handleOnFocus}
      onBlur={() => setActiveIndex(-1)}
      onKeyDown={handleKeyDown}
      onMouseDown={event => event.preventDefault()}
    >
      {options.map((option, index) => (
        <TabsTabStyled
          data-selector={`tab-${option.label}`}
          data-title={option.label}
          type="button"
          role="tab"
          key={`tab-${option.value}`}
          id={`tab-${index}`}
          aria-controls={contentPanelId}
          aria-selected={option.value === activeTab}
          $isActive={option.value === activeTab}
          $isFocussed={activeIndex === index}
          tabIndex={-1}
          disabled={!!option.isDisabled}
          lang={option.language}
        >
          {option.label}
        </TabsTabStyled>
      ))}
    </TabsStyled>
  );
};

export default Tabs;
